import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, User, ArrowRight, ArrowLeft, Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const AuthPage = () => {
  const { t } = useLanguage();
  const { login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });

  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        toast.success(`¡Bienvenido de nuevo!`);
      } else {
        await register(formData.name, formData.email, formData.password);
        toast.success(`¡Cuenta creada con éxito!`);
      }
      navigate(from, { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || 'Error de autenticación';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col px-4 py-6">
        {/* Back button - fixed at top */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[#38B2AC] mb-4 py-2"
          data-testid="back-btn"
        >
          <ArrowLeft size={22} />
          <span className="text-base font-medium">Volver</span>
        </button>

        <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-md">

          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-[#38B2AC] to-[#83C5BE] rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">T</span>
            </div>
            <span className="font-bold text-2xl text-[#1D1D1F]">The Mapp</span>
          </div>

          {/* Title */}
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1D1D1F] mb-2">
            {isLogin ? t('loginTitle') : t('registerTitle')}
          </h1>
          <p className="text-gray-500 mb-8">
            {isLogin
              ? 'Accede a tu cuenta para ver tus favoritos'
              : 'Crea una cuenta para guardar tus lugares favoritos'}
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name">{t('name')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Tu nombre"
                    value={formData.name}
                    onChange={handleChange}
                    required={!isLogin}
                    className="pl-10 bg-gray-50 border-transparent focus:border-[#38B2AC] focus:bg-white rounded-xl py-6"
                    data-testid="name-input"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">{t('email')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="pl-10 bg-gray-50 border-transparent focus:border-[#38B2AC] focus:bg-white rounded-xl py-6"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">{t('password')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  className="pl-10 bg-gray-50 border-transparent focus:border-[#38B2AC] focus:bg-white rounded-xl py-6"
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#38B2AC] hover:bg-[#005A63] text-white rounded-full py-6 text-lg font-medium"
              data-testid="submit-btn"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <>
                  {isLogin ? t('login') : t('register')}
                  <ArrowRight size={20} className="ml-2" />
                </>
              )}
            </Button>
          </form>

          {/* Google Sign-In divider */}
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-sm text-gray-400">o</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Google Sign-In button */}
          <button
            type="button"
            onClick={loginWithGoogle}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-gray-200 rounded-full hover:bg-gray-50 transition-colors"
            data-testid="google-login-btn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span className="font-medium text-gray-700">Continuar con Google</span>
          </button>

          {/* Toggle */}
          <p className="text-center mt-6 text-gray-500">
            {isLogin ? t('noAccount') : t('hasAccount')}{' '}
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="text-[#38B2AC] font-medium hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isLogin ? t('register') : t('login')}
            </button>
          </p>
        </div>
        </div>
      </div>

      {/* Right side - Image (hidden on mobile) */}
      <div className="hidden lg:block flex-1 relative">
        <img
          src="https://images.unsplash.com/photo-1650145202474-290f01b3c487?w=1200"
          alt="Tenerife"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-['Playfair_Display'] text-4xl font-bold mb-4">
            Descubre la magia de Tenerife
          </h2>
          <p className="text-lg text-white/90">
            Playas, volcán, gastronoía y mucho más te esperan
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
